import isEqual from 'lodash/isEqual'

import { DATE_VALUES } from 'utils/dateUtils'

import {
  CLEAR_DEEPFAKE_MEETINGS_FILTERS,
  DEFAULT_MEETINGS_ORDERING,
  DEFAULT_MEETINGS_PAGE_SIZE,
  GET_DEEPFAKE_ANALYTICS_ASYNC,
  GET_DEEPFAKE_ANALYTICS_PREVIOUS_ASYNC,
  GET_DEEPFAKE_CALL_DETAILS_ASYNC,
  GET_DEEPFAKE_MEETINGS_ASYNC,
  SET_DEEPFAKE_MEETINGS_FILTERS,
  SET_DEEPFAKE_MEETINGS_ORDERING,
  SET_DEEPFAKE_MEETINGS_PAGE,
  SET_DEEPFAKE_MEETINGS_PAGE_SIZE,
  SET_DEEPFAKE_MEETINGS_TIME_FILTER,
} from './meetingsConstants'

const initialState = {
  data: {
    items: [],
    count: 0,
    loader: false,
    error: null,
    page: 1,
    pageSize: DEFAULT_MEETINGS_PAGE_SIZE,
  },
  filters: {
    searchSubject: '',
    attendeeFilter: [],
    statusFilter: '',
    timeFilter: DATE_VALUES.WEEK,
    ordering: DEFAULT_MEETINGS_ORDERING,
    liveOnly: false,
  },
  insights: {
    scanned: 0,
    safe: 0,
    externals: 0,
    suspicious: 0,
  },
  analytics: {
    loader: false,
    error: null,
    data: null,
  },
  previousAnalytics: {
    loader: false,
    error: null,
    data: null,
  },
  callDetails: {
    loader: false,
    error: null,
    data: null,
  },
  falsePositive: {
    loader: false,
    error: null,
  },
  lastUpdated: new Date().toISOString(),
}

export default function meetingsReducer(state = initialState, action = {}) {
  if (!action?.type) return state

  switch (action.type) {
    case GET_DEEPFAKE_MEETINGS_ASYNC.INITIATE:
      return { ...state, data: { ...state.data, loader: true, error: null } }
    case GET_DEEPFAKE_MEETINGS_ASYNC.SUCCESS: {
      const items = action.payload?.items || []
      const count = action.payload?.total || 0
      const page = action.payload?.page || state.data.page
      const pageSize = action.payload?.size || state.data.pageSize
      const insights = action.payload?.insights || state.insights
      return {
        ...state,
        data: { ...state.data, items, count, page, pageSize, loader: false },
        insights,
      }
    }
    case GET_DEEPFAKE_MEETINGS_ASYNC.ERROR:
      return { ...state, data: { ...state.data, loader: false, error: action.error } }

    case GET_DEEPFAKE_ANALYTICS_ASYNC.INITIATE:
      return {
        ...state,
        analytics: { ...state.analytics, loader: true, error: null },
        previousAnalytics: { ...state.previousAnalytics, loader: true, error: null },
      }
    case GET_DEEPFAKE_ANALYTICS_ASYNC.SUCCESS:
      return { ...state, analytics: { ...state.analytics, loader: false, data: action.payload } }
    case GET_DEEPFAKE_ANALYTICS_ASYNC.ERROR:
      return { ...state, analytics: { ...state.analytics, loader: false, error: action.error } }

    case GET_DEEPFAKE_ANALYTICS_PREVIOUS_ASYNC.INITIATE:
      return {
        ...state,
        previousAnalytics: { ...state.previousAnalytics, loader: true, error: null },
      }
    case GET_DEEPFAKE_ANALYTICS_PREVIOUS_ASYNC.SUCCESS:
      return {
        ...state,
        previousAnalytics: { ...state.previousAnalytics, loader: false, data: action.payload },
      }
    case GET_DEEPFAKE_ANALYTICS_PREVIOUS_ASYNC.ERROR:
      return {
        ...state,
        previousAnalytics: { ...state.previousAnalytics, loader: false, error: action.error },
      }

    case GET_DEEPFAKE_CALL_DETAILS_ASYNC.INITIATE:
      // Silent refresh during polling: only show loader on the first fetch (no data yet)
      return {
        ...state,
        callDetails: {
          ...state.callDetails,
          loader: state.callDetails.data == null,
          error: null,
        },
      }
    case GET_DEEPFAKE_CALL_DETAILS_ASYNC.SUCCESS:
      // If payload is structurally identical to existing data, keep the same reference
      // so downstream selectors/memos don't re-trigger renders.
      if (isEqual(state.callDetails.data, action.payload)) {
        return state.callDetails.loader
          ? { ...state, callDetails: { ...state.callDetails, loader: false } }
          : state
      }
      return {
        ...state,
        callDetails: { ...state.callDetails, loader: false, data: action.payload },
      }
    case GET_DEEPFAKE_CALL_DETAILS_ASYNC.ERROR:
      return {
        ...state,
        callDetails: { ...state.callDetails, loader: false, error: action.error },
      }

    case SET_DEEPFAKE_MEETINGS_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        data: { ...state.data, loader: true, error: null },
      }
    case CLEAR_DEEPFAKE_MEETINGS_FILTERS:
      return {
        ...state,
        filters: {
          ...initialState.filters,
          timeFilter: state.filters.timeFilter, // Preserve current time filter
          ordering: DEFAULT_MEETINGS_ORDERING,
        },
        data: { ...state.data, loader: true, error: null },
      }
    case SET_DEEPFAKE_MEETINGS_ORDERING:
      return {
        ...state,
        filters: { ...state.filters, ordering: action.payload || '' },
        data: { ...state.data, loader: true, error: null },
      }
    case SET_DEEPFAKE_MEETINGS_PAGE:
      return {
        ...state,
        data: { ...state.data, page: action.payload || 1, loader: true, error: null },
      }
    case SET_DEEPFAKE_MEETINGS_PAGE_SIZE:
      return {
        ...state,
        data: {
          ...state.data,
          pageSize: action.payload || DEFAULT_MEETINGS_PAGE_SIZE,
          loader: true,
          error: null,
        },
      }
    case SET_DEEPFAKE_MEETINGS_TIME_FILTER:
      return {
        ...state,
        filters: { ...state.filters, timeFilter: action.payload },
        analytics: { ...state.analytics, loader: true, error: null },
      }
    case 'MARK_NOT_DEEPFAKE_REQUEST':
    case 'MARK_DEEPFAKE_REQUEST':
      return { ...state, falsePositive: { loader: true, error: null } }
    case 'MARK_NOT_DEEPFAKE_SUCCESS':
    case 'MARK_DEEPFAKE_SUCCESS':
      return { ...state, falsePositive: { loader: false, error: null } }
    case 'MARK_NOT_DEEPFAKE_FAILURE':
    case 'MARK_DEEPFAKE_FAILURE':
      return { ...state, falsePositive: { loader: false, error: action.error } }

    case 'UPDATE_DEEPFAKE_MEETINGS_LAST_UPDATED':
      return {
        ...state,
        lastUpdated: new Date().toISOString(),
      }
    case 'SET_DEEPFAKE_MEETINGS_LIVE_ONLY':
      return {
        ...state,
        filters: { ...state.filters, liveOnly: action.payload },
        data: { ...state.data, loader: true, error: null },
      }
    default:
      return state
  }
}
